import { Injectable } from '@angular/core';
import { Response, Headers, URLSearchParams } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/catch';
import { HttpService } from 'app/core/http/http.service';
import * as Models from 'app/core/models';
import { ProcessStage } from '../models';

/**
 * Provides a base for authentication workflow.
 * The Credentials interface as well as login/logout methods should be replaced with proper implementation.
 */
@Injectable()
export class ProcessService {

  constructor(private http: HttpService) {
  }

  getGroups(group: Models.ProcessGroups, pageSize?: number, pageNumber?: number, query?: string):
    Observable<Models.PagedList<Models.Process>> {

    if (!pageSize) {
      pageSize = 20;
    }

    if (!pageNumber) {
      pageNumber = 1;
    }

    const myParams = new URLSearchParams();
    myParams.append('pageSize', pageSize.toString());
    myParams.append('pageNumber', pageNumber.toString());
    myParams.append('group', group.toString());
    myParams.append('query', query);

    return this.http.get('process/groups', { params: myParams, cache: false })
      .map((res: Response) => res.json());
  }

  createProcess(context: Models.CreateProcess): Observable<Models.Process> {
    return this.http.post('process/editing/create', JSON.stringify(context), { cache: false })
      .map((res: Response) => res.json());
  }

  getDraft(context: number): Observable<Models.Process> {
    const myParams = new URLSearchParams();
    myParams.append('processId', context.toString());
    return this.http.get('process/editing/getdraft', { params: myParams, cache: false })
      .map((res: Response) => res.json());
  }

  getPublished(context: number): Observable<Models.Process> {
    const myParams = new URLSearchParams();
    myParams.append('processId', context.toString());
    return this.http.get('process/getPublished', { params: myParams, cache: false })
      .map((res: Response) => res.json());
  }

  saveDraft(context: Models.Process): Observable<Models.Process> {
    return this.http.post('process/editing/save', JSON.stringify(context), { cache: false })
      .map((res: Response) => res.json());
  }

  publish(context: Models.ProcessPublishModel): Observable<Models.Process> {
    return this.http.post('process/editing/publish', JSON.stringify(context), { cache: false })
      .map((res: Response) => res.json());
  }

  getProcessMedias(context: number): Observable<Array<Models.Media>> {
    const myParams = new URLSearchParams();
    myParams.append('processId', context.toString());
    return this.http.get('media/getProcessMedia', { params: myParams, cache: false })
      .map((res: Response) => res.json());
  }

  uploadMedia(context: FormData): Observable<Models.Media> {
    const headers = new Headers();
    const options = { headers: headers, cache: false, skipContentTypeSet: true };

    return this.http.post('media/uploadMedia', context, options)
      .map((res: Response) => res.json());
  }

  getExecutableProcess(context: number): Observable<Models.ExecutableProcess> {
    const myParams = new URLSearchParams();
    myParams.append('processId', context.toString());
    return this.http.get('process/execution/get', { params: myParams, cache: false })
      .map((res: Response) => res.json());
  }

  saveExecution(context: Models.SaveProcessExecutionModel): Observable<any> {
    return this.http.post('process/execution/save', JSON.stringify(context), { cache: false });
  }

  completeExecution(context: string): Observable<any> {
    return this.http.post('process/execution/complete', JSON.stringify({ processExecutionId: context }), { cache: false });
  }

  likeProcess(context: number): Observable<any> {
    return this.http.post('process/like', JSON.stringify({ processId: context }), { cache: false });
  }

  getMyProcesses(pageSize?: number): Observable<Models.Process[]> {
    if (!pageSize) {
      pageSize = 4;
    }

    const myParams = new URLSearchParams();
    myParams.append('pageSize', pageSize.toString());

    return this.http.get('process/executingOrEditingProcesses', { params: myParams, cache: false })
      .map((res: Response) => res.json());
  }

  getEditingProcesses(pageSize?: number, pageNumber?: number): Observable<Models.PagedList<Models.Process>> {
    if (!pageSize) {
      pageSize = 20;
    }

    if (!pageNumber) {
      pageNumber = 1;
    }

    const myParams = new URLSearchParams();
    myParams.append('pageSize', pageSize.toString());
    myParams.append('pageNumber', pageNumber.toString());

    return this.http.get('process/editingProcesses', { params: myParams, cache: false })
      .map((res: Response) => res.json());
  }

  getExecutingProcesses(pageSize?: number, pageNumber?: number): Observable<Models.PagedList<Models.Process>> {
    if (!pageSize) {
      pageSize = 20;
    }

    if (!pageNumber) {
      pageNumber = 1;
    }

    const myParams = new URLSearchParams();
    myParams.append('pageSize', pageSize.toString());
    myParams.append('pageNumber', pageNumber.toString());

    return this.http.get('process/executingProcesses', { params: myParams, cache: false })
      .map((res: Response) => res.json());
  }

  getMyPublishedProcesses(pageSize?: number, pageNumber?: number): Observable<Models.PagedList<Models.Process>> {
    if (!pageSize) {
      pageSize = 20;
    }

    if (!pageNumber) {
      pageNumber = 1;
    }

    const myParams = new URLSearchParams();
    myParams.append('pageSize', pageSize.toString());
    myParams.append('pageNumber', pageNumber.toString());

    return this.http.get('process/myPublished', { params: myParams, cache: false })
      .map((res: Response) => res.json());
  }

  getTagCategoryProcesses(tagId: number, pageSize?: number, pageNumber?: number): Observable<Models.PagedList<Models.Process>> {
    if (!pageSize) {
      pageSize = 20;
    }

    if (!pageNumber) {
      pageNumber = 1;
    }

    const myParams = new URLSearchParams();
    myParams.append('pageSize', pageSize.toString());
    myParams.append('pageNumber', pageNumber.toString());
    myParams.append('tagId', tagId.toString());

    return this.http.get('process/tagCategory', { params: myParams, cache: false })
      .map((res: Response) => res.json());
  }

  saveProcessTags(context: Models.SaveProcessTagsModel): Observable<any> {
    return this.http.post('process/editing/saveTags', JSON.stringify(context), { cache: false });
  }

  contactProcessCreator(context: Models.ContactProcessCreatorModel): Observable<any> {
    return this.http.post('process/execution/contactCreator', JSON.stringify(context), { cache: false });
  }

  deleteDraft(processId: number): Observable<any> {
    const myParams = new URLSearchParams();
    myParams.append('processId', processId.toString());
    return this.http.post('process/editing/deleteDraft', null, { params: myParams, cache: false });
  }

  unPublishProcess(processId: number): Observable<any> {
    const myParams = new URLSearchParams();
    myParams.append('processId', processId.toString());
    return this.http.post('process/editing/unPublish', null, { params: myParams, cache: false });
  }

  archiveProcess(processId: number): Observable<any> {
    const myParams = new URLSearchParams();
    myParams.append('processId', processId.toString());
    return this.http.post('process/editing/archive', null, { params: myParams, cache: false });
  }

  duplicateProcess(processId: number): Observable<Models.Process> {
    const myParams = new URLSearchParams();
    myParams.append('processId', processId.toString());
    return this.http.post('process/editing/duplicate', null, { params: myParams, cache: false })
      .map((res: Response) => res.json());
  }

  getMyArchivedProcesses(pageSize?: number, pageNumber?: number): Observable<Models.PagedList<Models.Process>> {
    if (!pageSize) {
      pageSize = 20;
    }

    if (!pageNumber) {
      pageNumber = 1;
    }

    const myParams = new URLSearchParams();
    myParams.append('pageSize', pageSize.toString());
    myParams.append('pageNumber', pageNumber.toString());

    return this.http.get('process/myArchived', { params: myParams, cache: false })
      .map((res: Response) => res.json());
  }

  canPublishProcess(process: Models.Process): boolean {
    let canPublish = true;

    if (!process.title || !process.description) {
      canPublish = false;
    }

    process.stages.forEach((stage) => {
      if (!stage.title || stage.title === '') {
        canPublish = false;
      }
      stage.steps.forEach((step) => {
        if (!step.stepTypeId || !step.title) {
          canPublish = false;
        }
      });
    });

    return canPublish;
  }
}



// WEBPACK FOOTER //
// C:/ProactiView/ProactiView-Client/src/app/core/process/process.service.ts